/**
 * Lightweight analytics shim — tool-agnostic.
 *
 * Sprint F1.d — emits structured events without coupling to any specific
 * analytics vendor. The payload is dispatched as a `CustomEvent('verix:analytics')`
 * on `window`; downstream integrations (Posthog, Segment, Amplitude, a custom
 * backend endpoint) attach a single listener and translate to their own API.
 *
 * Why not call the vendor SDK directly: commits to a vendor we haven't chosen
 * yet; CustomEvent keeps the call sites free to swap.
 *
 * Destino actual: Sentry, vía `captureAnalyticsEvent` (el listener se engancha en
 * `main.jsx`). Es PROVISIONAL y una concesión consciente — este comentario decía
 * antes que los eventos de negocio necesitan su propio pipeline y retención, y
 * sigue siendo cierto: Sentry está pensado para errores. Pero ya está configurado
 * y pagado, la checklist de Powens (R135) lo cita como ejemplo válido, y el bus
 * hace que cambiarlo cueste un solo enganche. Elegir un proveedor de producto
 * (Jitsu, PostHog, Segment) y mover ese enganche es trabajo pendiente, no un
 * rediseño: ningún `track()` se entera.
 *
 * Consentimiento: el gate vive en el listener (`captureAnalyticsEvent`), no aquí.
 * `track()` emite siempre; quien decide si eso sale de la máquina es el destino.
 *
 * Call signature is deliberately flat so that future vendor migrations don't
 * need to touch every emission point.
 */

const EVENT_BUS_NAME = 'verix:analytics';

/**
 * Track a single event. Never throws.
 *
 * @param {string} name  Event name in dot-notation (e.g. 'ai.message.sent')
 * @param {object} [props] Optional flat payload (string/number/boolean values)
 *
 * Recommended vocabulary for AI events:
 *   ai.message.sent          { conversation_id, message_len, module }
 *   ai.message.received      { conversation_id, tools_used, duration_ms, chart: boolean }
 *   ai.action.approved       { conversation_id, action_type }
 *   ai.action.rejected       { conversation_id, action_type }
 *   ai.feedback.given        { conversation_id, kind: 'positive' | 'negative' }
 *   ai.error                 { conversation_id?, error_code, message }
 *   ai.scheduled_action.created  { tool, cooldown_minutes }
 *   ai.scheduled_action.deleted  {}
 */
export function track(name, props = {}) {
  try {
    if (typeof window === 'undefined') return;
    const detail = {
      name,
      ts: Date.now(),
      ...props,
    };
    window.dispatchEvent(new CustomEvent(EVENT_BUS_NAME, { detail }));

    // Dev convenience: log to console only when explicitly enabled via a
    // localStorage flag so production builds stay silent.
    if (typeof localStorage !== 'undefined' && localStorage.getItem('verix_analytics_debug') === '1') {
       
      console.debug('[analytics]', name, props);
       
    }
  } catch {
    /* never break the caller */
  }
}

/**
 * Subscribe to all analytics events. Returns an unsubscribe function.
 * Use this when wiring up a real vendor (Posthog, Segment, etc.).
 *
 * Example (Posthog):
 *   subscribe(({ name, ts, ...props }) => {
 *     posthog.capture(name, { $timestamp: ts, ...props });
 *   });
 */
export function subscribe(listener) {
  if (typeof window === 'undefined') return () => {};
  const handler = (evt) => {
    try { listener(evt.detail); } catch { /* listener must not break bus */ }
  };
  window.addEventListener(EVENT_BUS_NAME, handler);
  return () => window.removeEventListener(EVENT_BUS_NAME, handler);
}
